import { useContext, useState } from 'react';
import { Alert, Badge, Button, ListGroup, ListGroupItem, Table } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import UWEFormContext from '../../../context/UWEFormContext';
import { AuthContext } from '../../../context/AuthContext';
import Loader from '../../Loader';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { OcifXlsxApi, OcifInventoryFileSummary, OcifXlsxApiError, triggerBlobDownload } from '../../../api/OcifXlsxApi';
import { FieldProps } from '@rjsf/utils';
import useResourceLoader from '../../../util/useResourceLoader';
import { AccordionItem } from '../../ui/Accordion';
import Icon from 'src/components/Icon';
import { StatusBadge } from '../OcifBulkUpload/StatusBadge';
import { isAmendmentEntity } from '../OcifBulkUpload/util';
import { ScheduleLinkList } from '../OcifBulkUpload/ScheduleLinkList';
import { useScheduleStatuses } from '../OcifBulkUpload/useScheduleStatuses';
import { useOpenScheduleTab } from '../OcifBulkUpload/useOpenScheduleTab';
import { ReviewStatus } from './types';
import { ReportInfoCard } from './ReportInfoCard';
import { FlagsNotesCard } from './FlagsNotesCard';

dayjs.extend(localizedFormat);

const REVIEWER_ROLES = ['Analista Financiero', 'Supervisor de Analisis Financiero', 'Administrador'];

function rowStatus(row: OcifInventoryFileSummary): ReviewStatus {
  if (row.nothingToReport) return 'Nothing to Report';
  return row.fileId ? 'Uploaded' : 'Not Uploaded';
}

function OcifReviewSummaryPanel({ registry, fieldPathId }: FieldProps) {
  const UweFormContext = useContext(UWEFormContext);
  const currentEntity = UweFormContext?.currentEntity;
  const entityId = currentEntity?.id as number | undefined;
  const isMobile = useIsMobile();
  const { i18n } = useLingui();

  const auth = useContext(AuthContext);
  const actualOrgId = auth?.user?.user?.actualOrganizationalUnitId;
  const userRoleNames = (auth?.user?.organizationalUnitRoles ?? [])
    .filter(our => our.organizationalUnitId === actualOrgId)
    .map(our => our.role?.name);
  const isReadOnly = !REVIEWER_ROLES.some(role => userRoleNames.includes(role));

  const [summaries, loading = true, error] = useResourceLoader(async () => {
    if (!entityId) return undefined;
    return OcifXlsxApi.getInventoryFileSummaries(entityId);
  }, [entityId]);

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { financial: financialSchedules } = useScheduleStatuses(registry);
  const openScheduleTab = useOpenScheduleTab(fieldPathId);

  const rows = summaries ?? [];
  const isAmendment = isAmendmentEntity(currentEntity);

  const handleDownload = async (fileId: number, fileName: string | null) => {
    if (!entityId || downloadingId) return;
    setDownloadingId(fileId);
    try {
      const blob = await OcifXlsxApi.downloadInventoryFile(entityId, fileId);
      triggerBlobDownload(blob, fileName ?? `inventory-${fileId}.xlsx`);
    } catch (err) {
      alert((err as Error)?.message ?? 'Could not download file.');
    } finally {
      setDownloadingId(null);
    }
  };

  const renderFileCell = (row: OcifInventoryFileSummary) => {
    if (row.nothingToReport) {
      return (
        <span className="text-muted small">
          <Icon icon="MinusCircle" className="mr-1" />
          <Trans>Marked as nothing to report</Trans>
        </span>
      );
    }
    if (row.fileId) {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-primary small font-bold">{row.fileName}</span>
          <span className="text-muted small">
            <Trans>Uploaded:</Trans>{' '}
            {row.uploadedAt &&
              dayjs(row.uploadedAt)
                .locale(i18n.locale)
                .format('ll')}
            {row.uploadedByName && <> · {row.uploadedByName}</>}
          </span>
        </div>
      );
    }
    return (
      <span className="text-muted small">
        <Trans>No file uploaded yet</Trans>
      </span>
    );
  };

  const renderDownloadBtn = (fileId: number | null, fileName: string | null) =>
    fileId ? (
      <Button color="primary" outline size="sm" disabled={downloadingId === fileId} onClick={() => handleDownload(fileId, fileName)}>
        {downloadingId === fileId ? <Loader dots sm inline size={14} /> : <Icon icon="Download" />}
      </Button>
    ) : null;

  // Amendments only: what the report had when the amendment was created.
  const renderOriginalCell = (row: OcifInventoryFileSummary) => {
    if (row.originalNothingToReport) {
      return (
        <span className="text-muted small">
          <Trans>Marked as nothing to report</Trans>
        </span>
      );
    }
    if (row.originalFileId) {
      return (
        <div className="flex items-center gap-2">
          <span className="small">{row.originalFileName}</span>
          {renderDownloadBtn(row.originalFileId, row.originalFileName)}
        </div>
      );
    }
    return (
      <span className="text-muted small">
        <Trans>No file uploaded</Trans>
      </span>
    );
  };

  const renderAmendedBadge = (row: OcifInventoryFileSummary) =>
    isAmendment && row.changedInAmendment ? (
      <Badge color="warning" pill className="ml-2">
        <Trans>Amended</Trans>
      </Badge>
    ) : null;

  // The status comes off the thrown error, not off its text: for every status the backend
  // handles deliberately the body is { error: <AppException message> }, so the message is that
  // prose and never mentions the status code.
  const renderErrorDetail = () => {
    switch (error instanceof OcifXlsxApiError ? error.status : undefined) {
      case 400:
        return <Trans>This report is not one that can have inventory schedules.</Trans>;
      case 403:
        return <Trans>Your user does not have permission to view this report's inventory schedules.</Trans>;
      case 404:
        return <Trans>This report references data that no longer exists.</Trans>;
      default:
        return <Trans>The server could not complete the request. Try again, and report it if it keeps happening.</Trans>;
    }
  };

  return (
    <div className="review-summary-panel">
      <div className="grid gap-4 mt-4 md:grid-cols-2">
        <ReportInfoCard />
        <FlagsNotesCard />
      </div>

      {!isReadOnly && (
        <AccordionItem
          title={
            <span className="form-subtitle">
              <Trans>Review Instructions</Trans>
            </span>
          }
          className="mb-4 mt-4"
        >
          <div className="px-3 pb-4 pt-2 pl-4" style={{ lineHeight: 2 }}>
            <p className="mb-2">
              <Trans>To review this report, follow these steps:</Trans>
            </p>
            <ol className="mb-0 pl-3">
              <li className="mb-2">
                <Trans>
                  Open each schedule from the list below (or the tabs above) and verify that the submitted information is complete and accurate.
                </Trans>
              </li>
              <li className="mb-2">
                <Trans>
                  If this report includes inventory schedules, they are listed at the bottom of this section. Review their contents by downloading the
                  corresponding Excel file for each schedule.
                </Trans>
              </li>
              <li className="mb-2">
                <Trans>
                  Review any red flags (data remarks) generated by the system — the card above shows how many are still active — and add any
                  additional remarks identified during your review.
                </Trans>
              </li>
              <li>
                <Trans>
                  Upon completing your review, either approve the report or return it to the filing institution, in accordance with your flags.
                </Trans>
              </li>
            </ol>
          </div>
        </AccordionItem>
      )}

      {financialSchedules.length > 0 && (
        <AccordionItem
          defaultOpen
          title={
            <span className="form-subtitle">
              <Trans>Schedules</Trans>
            </span>
          }
          className="mb-4 mt-4"
        >
          <ScheduleLinkList rows={financialSchedules} onRowClick={row => openScheduleTab(row.key)} />
        </AccordionItem>
      )}

      <AccordionItem
        defaultOpen
        title={
          <span className="form-subtitle">
            <Trans>Inventory Schedules</Trans>
          </span>
        }
        className="mb-4 mt-4"
      >
        {loading && (
          <div className="py-4">
            <Loader spinner sm>
              <Trans>Loading inventory schedules…</Trans>
            </Loader>
          </div>
        )}
        {!loading && error && (
          <Alert color="danger" className="m-3" fade={false}>
            <div>
              <Trans>Could not load inventory schedules.</Trans> {renderErrorDetail()}
            </div>
            {error.message && <div className="small mt-1">{error.message}</div>}
          </Alert>
        )}
        {!loading && !error && (
          <>
            {isMobile ? (
              <ListGroup flush>
                {rows.map(row => (
                  <ListGroupItem key={row.scheduleType} className="py-3 px-0">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <strong>
                        {row.title}
                        {row.required && (
                          <span className="text-muted small ml-1">
                            (<Trans>Required</Trans>)
                          </span>
                        )}
                        {renderAmendedBadge(row)}
                      </strong>
                      <StatusBadge status={rowStatus(row)} />
                    </div>
                    <div className="mb-2">{renderFileCell(row)}</div>
                    <div>{renderDownloadBtn(row.fileId, row.fileName)}</div>
                    {isAmendment && (
                      <div className="mt-2 small">
                        <span className="text-muted">
                          <Trans>Original:</Trans>
                        </span>{' '}
                        {renderOriginalCell(row)}
                      </div>
                    )}
                  </ListGroupItem>
                ))}
                {rows.length === 0 && (
                  <ListGroupItem className="text-center text-muted small py-3">
                    <Trans>No schedules configured</Trans>
                  </ListGroupItem>
                )}
              </ListGroup>
            ) : (
              <Table size="sm" className="align-middle mt-2 mb-0">
                <thead>
                  <tr>
                    <th>
                      <Trans>Schedule</Trans>
                    </th>
                    <th className="text-center" style={{ width: 120, textAlign: 'left' }}>
                      <Trans>Status</Trans>
                    </th>
                    <th style={{ width: '40%', textAlign: 'left' }}>
                      <Trans>File</Trans>
                    </th>
                    <th className="text-center" style={{ width: 100, textAlign: 'left' }}>
                      <Trans>Download</Trans>
                    </th>
                    {isAmendment && (
                      <th style={{ width: '25%', textAlign: 'left' }}>
                        <Trans>Original</Trans>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map(row => (
                      <tr key={row.scheduleType}>
                        <td>
                          <strong>{row.title}</strong>
                          {row.required && (
                            <span className="text-muted small ml-1">
                              (<Trans>Required</Trans>)
                            </span>
                          )}
                          {renderAmendedBadge(row)}
                        </td>
                        <td className="text-center">
                          <StatusBadge status={rowStatus(row)} />
                        </td>
                        <td>{renderFileCell(row)}</td>
                        <td className="text-center">{renderDownloadBtn(row.fileId, row.fileName)}</td>
                        {isAmendment && <td>{renderOriginalCell(row)}</td>}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={isAmendment ? 5 : 4} className="text-center text-muted small py-3">
                        <Trans>No schedules configured</Trans>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </>
        )}
      </AccordionItem>
    </div>
  );
}

export default OcifReviewSummaryPanel;
